import { Component, inject, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule, Table } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProductResponse, ShoppingCategory, UpdateProductRequest } from '@app/shared/interfaces/cost-type-config.interface';
import { CostTypeConfigService } from '@app/shared/services/cost-type-config.service';
import { finalize } from 'rxjs';
import { Select } from 'primeng/select';
import { MessageService } from 'primeng/api';
import * as xlsx from 'xlsx';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-product-management',
    imports: [
        CommonModule,
        TableModule,
        ProgressSpinnerModule,
        ButtonModule,
        FormsModule,
        DialogModule,
        InputTextModule,
        Select,
        ColumnFilterComponent,
        PageHeaderComponent
    ],
    templateUrl: './product-management.component.html',
    styleUrl: './product-management.component.scss'
})
export class ProductManagementComponent implements OnInit {
    products: ProductResponse[] = [];
    loading: boolean = true;
    @ViewChild('dt') dt!: Table;
    displayDialog = false;
    categories: ShoppingCategory[] = [];
    costTypeConfigService = inject(CostTypeConfigService);
    dialogMode: 'add' | 'edit' = 'add';
    cols: any[] = [
        { field: 'categoryName', header: 'Product Category' },
        { field: 'productName', header: 'Product Name' }
    ];
    dialogHeader = '';

    formData: UpdateProductRequest = {
        productId: 0,
        productName: '',
        categoryId: 0
    };
    exportXlsx() {
        try {
            if (!this.dt || !this.dt.value) {
                console.error('Table reference not found or no data');
                return;
            }
            const data = this.dt.value.map((row: any) => {
                return {
                    Category: row?.categoryName ?? '',
                    'Product Name': row?.productName ?? ''
                };
            });
            const ws: xlsx.WorkSheet = xlsx.utils.json_to_sheet(data);
            const wb: xlsx.WorkBook = xlsx.utils.book_new();
            xlsx.utils.book_append_sheet(wb, ws, 'Report');
            xlsx.writeFile(wb, 'product-management.xlsx');
        } catch (error) {
            console.error('Export failed:', error);
        }
    }
    ngOnInit() {
        this.loadCategories();
        this.loadProducts();
    }

    constructor(
        private messageService: MessageService,
        private cd: ChangeDetectorRef
    ) {}
    loadCategories(): void {
        this.costTypeConfigService.getProductsCategory().subscribe({
            next: (categories) => {
                this.categories = categories;
                this.cd.detectChanges();
            },
            error: (err) => {
                console.error(err);
            }
        });
    }

    loadProducts(): void {
        this.loading = true;
        this.cd.detectChanges();

        this.costTypeConfigService
            .getCostProducts()
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this.cd.detectChanges();
                })
            )
            .subscribe({
                next: (products) => {
                    this.products = products;
                    this.cd.detectChanges();
                },
                error: (err) => {
                    console.error(err);
                }
            });
    }

    openDialog(mode: 'add' | 'edit', product?: ProductResponse) {
        this.dialogMode = mode;
        this.displayDialog = true;

        if (mode === 'add') {
            this.dialogHeader = 'Add New';
            this.formData = { productId: 0, productName: '', categoryId: 0 };
        } else {
            this.dialogHeader = 'Edit';

            if (!product) {
                console.error('Edit mode requires a product');
                return;
            }

            this.formData = {
                productId: product.productId ?? 0,
                productName: product.productName ?? '',
                categoryId: product.categoryId ?? 0
            };
        }
    }

    save() {
        if (this.dialogMode === 'add') {
            this.costTypeConfigService
                .addCostProduct({
                    productName: this.formData.productName,
                    categoryId: this.formData.categoryId
                })
                .subscribe({
                    next: (res) => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'The product has been created succesfully.'
                        });
                        this.loadProducts();
                    },
                    error: (err) => {
                        console.error(err);
                    }
                });
        } else {
            this.costTypeConfigService.editCostProduct(this.formData).subscribe({
                next: (res) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'The product has been updated succesfully.'
                    });
                    this.loadProducts();
                },
                error: (err) => {
                    console.error(err);
                }
            });
        }

        this.displayDialog = false;
    }

    hideDialog() {
        this.displayDialog = false;
    }
}
